import { motion } from 'motion/react';
import { PROMO_BANNER } from '../../content/site';
import { spring } from '../../lib/motion';
import { plausibleEventClass } from '../../lib/plausible';

/**
 * HP gaming promo banner (HX-42) — sits under the hosts triangle in every
 * phase, but only while the server says the scheduled window is open
 * (HX-43). HomePage owns that gate; this component just renders.
 *
 * The designer never finished this piece, so the layout is our
 * extrapolation from the client's PSD (client direction 2026-08-12: "take
 * some liberties"). Deliberate deviations, all confirmed with the client:
 *  - The card is a PLAIN rounded-rect black container, not the library's
 *    notched Product Black Box, and carries no diamond ornaments. All three
 *    references agree on it — the designer's own Figma start, the PSD, and
 *    the panel crops themselves (the desktop panel ships with SQUARE corners,
 *    which only makes sense if the card clips them).
 *  - Because the card clips (`overflow-hidden` + its own radius), both panels
 *    sit full-bleed and their baked corners are irrelevant.
 *
 * Proportions come from the PSD composites: the desktop panel is the right
 * 66.06% of a 1370×456 card (the remaining 33.94% is the black text zone the
 * copy was designed against), and the mobile panel is the top 57.06% of a
 * 704×722 card. Rather than lock the card to those aspect ratios — which
 * would overflow the moment the copy needed one more line — each panel holds
 * its own aspect and the text column is free to grow the card. At every
 * breakpoint the panel still covers its share via object-cover.
 *
 * Breakpoint: stacked below `md`, side-by-side at `md` and up — but the
 * column split widens to 45/55 between md and lg. Holding the PSD ratio all
 * the way down leaves a ~250px text column at 768px, and stacking that far up
 * instead makes the card taller than a tablet viewport.
 */

/** Both panels, for HomePage's ready-gated Reveal. */
export const PROMO_BANNER_ART = [
  '/assets/images/banners/gaming-promo.webp',
  '/assets/images/banners/gaming-promo-mobile.webp',
];

/**
 * Tracked as `Promo Click`, its own Plausible Goal rather than a `CTA Click`
 * location: this is the page's only commercial conversion — it leaves for the
 * HP store against a discount code, where every other CTA sends people to
 * Twitch or into the giveaway. Folding it into CTA Click would bury the offer's
 * performance inside a prop breakdown of a goal that means something else.
 * `code` rides along so a future second offer stays attributable.
 */
function ShopNowButton() {
  return (
    <motion.a
      href={PROMO_BANNER.href}
      target="_blank"
      rel="noreferrer"
      aria-label={`${PROMO_BANNER.cta} at HP (opens in a new tab)`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.97 }}
      transition={spring.tap}
      className={`inline-block rounded-[0.3em] bg-white px-[1.4em] py-[0.7em] text-center font-heading text-[0.85em] uppercase leading-none tracking-wider text-black transition-shadow duration-200 hover:shadow-[0_0_18px_0_rgba(255,255,255,0.55)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${plausibleEventClass(
        'Promo Click',
        { location: 'promo-banner', code: PROMO_BANNER.code },
      )}`}
    >
      {PROMO_BANNER.cta}
    </motion.a>
  );
}

/** Heading → body → code → CTA → fine print. Sized in `em` off the column's
 *  own font-size so one scale knob drives the whole block at each variant. */
function Copy() {
  return (
    <div className="flex flex-col items-start">
      <p className="font-body text-[1.55em] font-bold leading-tight text-brand-white">
        {PROMO_BANNER.heading}
      </p>
      <p className="mt-[0.85em] max-w-[26em] font-body text-[0.82em] leading-snug text-brand-white">
        {PROMO_BANNER.body}
      </p>
      <p className="mt-[0.2em] font-body text-[0.82em] leading-snug text-brand-white">
        {PROMO_BANNER.codeLabel}{' '}
        {/* The code is the actionable part of the offer — selectable so it can
            be copied, and colored brand red as the PSD shows. */}
        <span className="select-all font-semibold text-brand-red">{PROMO_BANNER.code}</span>
      </p>
      <div className="mt-[1.35em]">
        <ShopNowButton />
      </div>
      <p className="mt-[1.1em] font-body text-[0.6em] leading-none text-brand-muted">
        {PROMO_BANNER.finePrint}
      </p>
    </div>
  );
}

export function PromoBanner() {
  return (
    <section aria-labelledby="promo-heading" className="w-full max-w-content px-4">
      <h2 id="promo-heading" className="sr-only">
        {PROMO_BANNER.heading}
      </h2>

      {/* Side-by-side — text column left, product panel right. The column
          split widens at tablet sizes (45/55) and settles to the PSD's
          33.94/66.06 at lg: holding the design ratio all the way down
          squeezes the text column to ~250px at 768, which crushes the
          three-line body. The panel is object-cover, so it simply crops
          tighter at the wider splits. */}
      <div className="shadow-promo-card hidden overflow-hidden rounded-[1.97cqw] bg-black [container-type:inline-size] md:block">
        <div className="flex w-full items-stretch">
          <div className="flex flex-[45] items-center py-[2.6cqw] pl-[3.2cqw] pr-[1.6cqw] text-[clamp(0.8rem,2.2cqw,1.25rem)] lg:flex-[33.94]">
            <Copy />
          </div>
          <div className="flex-[55] lg:flex-[66.06]">
            <img
              src={PROMO_BANNER_ART[0]}
              alt={PROMO_BANNER.imageAlt}
              decoding="async"
              fetchpriority="low"
              className="h-full w-full object-cover"
              style={{ aspectRatio: '905 / 456' }}
            />
          </div>
        </div>
      </div>

      {/* Stacked — product panel on top, copy beneath. Capped and centered
          like CellGrid's mobile variant so the card can't balloon as the
          viewport approaches the md switch. */}
      <div className="shadow-promo-card mx-auto max-w-[520px] overflow-hidden rounded-[3.84cqw] bg-black [container-type:inline-size] md:hidden">
        <img
          src={PROMO_BANNER_ART[1]}
          alt={PROMO_BANNER.imageAlt}
          decoding="async"
          fetchpriority="low"
          className="w-full object-cover"
          style={{ aspectRatio: '704 / 412' }}
        />
        <div className="px-[6cqw] pb-[7cqw] pt-[6cqw] text-[clamp(0.95rem,4.2cqw,1.4rem)]">
          <Copy />
        </div>
      </div>
    </section>
  );
}
